import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Shield, Lock, Eye, Users, Bell, Trash2, Mail, Phone, MapPin } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-12 pt-28">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <Shield className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-600">Last Updated: April 29, 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Welcome to ReBusi ("we," "our," or "us"). We are committed to protecting your privacy and ensuring 
              the security of your personal information. This Privacy Policy explains how we collect, use, disclose, 
              and safeguard your information when you use our website, mobile application, and services, including 
              our WhatsApp Business communications.
            </p>
            <p className="text-slate-700 leading-relaxed">
              By using ReBusi's services, you agree to the collection and use of information in accordance with 
              this policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Eye className="w-6 h-6 text-orange-500" />
              2. Information We Collect
            </h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">2.1 Personal Information</h3>
            <p className="text-slate-700 leading-relaxed mb-3">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Account Information:</strong> Name, email address, phone number, password</li>
              <li><strong>Profile Information:</strong> Business details, location, industry preferences</li>
              <li><strong>Business Listing Data:</strong> Business name, description, financial information, documents</li>
              <li><strong>Transaction Data:</strong> Deal values, payment information, escrow details</li>
              <li><strong>Communication Data:</strong> Messages, enquiries, consultation bookings</li>
              <li><strong>WhatsApp Data:</strong> Phone number, message content, delivery status when you communicate with us via WhatsApp</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent, click patterns</li>
              <li><strong>Location Data:</strong> General location based on IP address</li>
              <li><strong>Cookies and Tracking:</strong> Session data, preferences, analytics</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-orange-500" />
              3. How We Use Your Information
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve our marketplace services</li>
              <li><strong>Account Management:</strong> To create and manage your account, verify identity</li>
              <li><strong>Communication:</strong> To send transactional messages, updates, and marketing communications (with consent)</li>
              <li><strong>WhatsApp Communications:</strong> To send order confirmations, deal updates, consultation reminders, and respond to your queries via WhatsApp Business API</li>
              <li><strong>Matching Services:</strong> To connect buyers with relevant businesses and sellers</li>
              <li><strong>Due Diligence:</strong> To facilitate document verification and agreement processes</li>
              <li><strong>Analytics:</strong> To analyze usage patterns and improve user experience</li>
              <li><strong>Security:</strong> To detect and prevent fraud, abuse, and security threats</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          {/* WhatsApp Business API */}
          <section className="bg-green-50 p-6 rounded-lg border border-green-200">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Phone className="w-6 h-6 text-green-600" />
              4. WhatsApp Business Communications
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              When you choose to communicate with us via WhatsApp, the following applies:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>We use WhatsApp Business API to send you important updates about your deals, listings, and consultations</li>
              <li>Your phone number and message content are processed in accordance with WhatsApp's terms of service</li>
              <li>We may send you transactional messages (deal updates, booking confirmations) and, with your consent, promotional messages</li>
              <li>You can opt-out of promotional messages at any time by replying "STOP" or contacting us</li>
              <li>Message history may be retained for customer support and compliance purposes</li>
              <li>We do not share your WhatsApp data with third parties for their marketing purposes</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Opt-Out:</strong> To stop receiving WhatsApp messages, reply "STOP" to any message or 
              contact us at <a href="mailto:privacy@rebusi.io" className="text-green-600 underline">privacy@rebusi.io</a>
            </p>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-orange-500" />
              5. Information Sharing and Disclosure
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We may share your information in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>With Other Users:</strong> Buyers and sellers can view relevant listing/requirement details to facilitate transactions</li>
              <li><strong>Service Providers:</strong> With third-party vendors who assist in operating our platform (payment processors, email services, cloud hosting)</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you have given explicit consent to share information</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>We do NOT sell your personal information to third parties.</strong>
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Security</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We implement appropriate technical and organizational measures to protect your personal information:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>Encryption of data in transit (SSL/TLS) and at rest</li>
              <li>Secure password hashing using industry-standard algorithms</li>
              <li>Regular security assessments and vulnerability testing</li>
              <li>Access controls limiting employee access to personal data</li>
              <li>Secure escrow and payment processing through verified providers</li>
              <li>Moderated messaging system to prevent unauthorized contact sharing</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              While we strive to protect your information, no method of transmission over the Internet is 100% secure. 
              We cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Retention</h2>
            <p className="text-slate-700 leading-relaxed">
              We retain your personal information for as long as necessary to provide our services and fulfill 
              the purposes described in this policy. Specifically:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4 mt-3">
              <li><strong>Account Data:</strong> Retained while your account is active, plus 3 years after deletion</li>
              <li><strong>Transaction Records:</strong> Retained for 7 years for legal and tax compliance</li>
              <li><strong>Communication Logs:</strong> Retained for 2 years for dispute resolution</li>
              <li><strong>WhatsApp Messages:</strong> Retained for 1 year or as required for ongoing support</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Bell className="w-6 h-6 text-orange-500" />
              8. Your Rights and Choices
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and personal data</li>
              <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Withdraw Consent:</strong> Revoke consent for specific data processing activities</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              To exercise these rights, contact us at <a href="mailto:privacy@rebusi.io" className="text-orange-500 underline">privacy@rebusi.io</a>
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Cookies and Tracking Technologies</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>Keep you logged in and remember your preferences</li>
              <li>Analyze traffic and usage patterns</li>
              <li>Personalize content and recommendations</li>
              <li>Measure advertising effectiveness</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              You can control cookies through your browser settings. Disabling cookies may limit some features of our service.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-700 leading-relaxed">
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect 
              personal information from children. If you believe we have collected information from a minor, 
              please contact us immediately.
            </p>
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. International Data Transfers</h2>
            <p className="text-slate-700 leading-relaxed">
              Your information may be transferred to and processed in countries other than India. We ensure 
              appropriate safeguards are in place to protect your data in accordance with this policy and 
              applicable data protection laws.
            </p>
          </section>

          {/* Policy Updates */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Changes to This Policy</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4 mt-3">
              <li>Posting the updated policy on our website</li>
              <li>Updating the "Last Updated" date at the top</li>
              <li>Sending you an email notification (for material changes)</li>
              <li>Displaying a notice on our platform</li>
            </ul>
          </section>

          {/* Contact Information */}
          <section className="bg-slate-100 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-orange-500" />
              13. Contact Us
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-500" />
                <span className="text-slate-700">privacy@rebusi.io</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-500" />
                <span className="text-slate-700">+91 99999 99999</span>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-500 mt-1" />
                <span className="text-slate-700">
                  ReBusi Technologies Pvt. Ltd.<br />
                  123 Business Park, Sector 15<br />
                  Mumbai, Maharashtra 400001, India
                </span>
              </div>
            </div>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Data Protection Officer:</strong> dpo@rebusi.io
            </p>
          </section>

          {/* Grievance Redressal */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Grievance Redressal</h2>
            <p className="text-slate-700 leading-relaxed">
              In accordance with the Information Technology Act, 2000 and rules made thereunder, the name and 
              contact details of the Grievance Officer are provided above. If you have any complaints or concerns 
              regarding the processing of your personal data, you may contact our Grievance Officer who will 
              acknowledge your complaint within 24 hours and resolve it within 30 days.
            </p>
          </section>

        </div>

        {/* Footer Note */}
        <p className="text-center text-slate-500 text-sm mt-8">
          This Privacy Policy is effective as of April 29, 2026 and will remain in effect except with respect 
          to any changes in its provisions in the future.
        </p>
      </div>

      <Footer />
    </div>
  );
}
